<app-league-page-hero
  title="Tournament"
  subtitle="Follow tournament schedules, brackets, and teams."
  [links]="tab === 'bracket' ? [] : tournamentLinks"
></app-league-page-hero>

<main class="tournament-public-page" [attr.aria-busy]="isLoading">
  <section class="public-state" *ngIf="isLoading" role="status" aria-live="polite">
    <mat-progress-spinner mode="indeterminate" diameter="44"></mat-progress-spinner>
    <p>Loading tournaments...</p>
  </section>

  <section class="public-state public-state--error" *ngIf="!isLoading && loadError" role="alert">
    <mat-icon>error_outline</mat-icon>
    <h2>Unable to load tournaments</h2>
    <p>{{ loadError }}</p>
    <button type="button" (click)="loadTournaments()">Retry</button>
  </section>

  <section class="public-state" *ngIf="!isLoading && !loadError && !selected" role="status">
    <mat-icon>emoji_events</mat-icon>
    <h2>No tournaments are available</h2>
    <p>Published tournaments will appear here.</p>
  </section>

  <section class="tournament-control" *ngIf="!isLoading && !loadError && selected">
    <div class="control-copy"><span>Find Games</span><strong>Upcoming matchups</strong></div>
    <label>Tournament<select [(ngModel)]="selected" (ngModelChange)="onTournamentChange()"><option *ngFor="let tournament of tournaments" [ngValue]="tournament">{{ tournament.name }}</option></select></label>
    <label>Division<select [(ngModel)]="selectedDivisionId" (ngModelChange)="selectedTeamId = ''"><option value="">All Divisions</option><option *ngFor="let division of selected.divisions" [value]="division.id">{{ division.name }}</option></select></label>
    <label>Team<select [(ngModel)]="selectedTeamId" [disabled]="!selectedDivisionId"><option value="">{{ selectedDivisionId ? 'All Teams' : 'Select a division first' }}</option><option *ngFor="let team of teams()" [value]="team.id">{{ team.name }}</option></select></label>
  </section>
  <section class="tournament-panel" *ngIf="!isLoading && !loadError && selected">
    <h2>{{ selected.name }}</h2>

    <div *ngIf="tab === 'schedule'" class="date-groups">
      <section class="date-section" *ngFor="let group of scheduleDayGroups()">
        <div class="date-header">
          <div>
            <span class="date-kicker">{{ formatDatePart(group.date, 'weekday') }}</span>
            <h2>{{ formatDatePart(group.date, 'date') }}</h2>
          </div>
        </div>

        <ng-container *ngFor="let phase of schedulePhases(group.times)">
          <header class="pool-play-section-header" *ngIf="phase.kind === 'pool'">
            <span class="pool-play-section-icon" aria-hidden="true"><mat-icon>groups</mat-icon></span>
            <span class="pool-play-section-copy">
              <span>Group stage</span>
              <strong>Pool Play</strong>
            </span>
            <span class="pool-play-section-note">Compete for playoff seeding</span>
          </header>

          <header class="playoff-section-header" *ngIf="phase.kind === 'playoff'">
            <span class="playoff-section-icon" aria-hidden="true"><mat-icon>emoji_events</mat-icon></span>
            <span class="playoff-section-copy">
              <span>Knockout stage</span>
              <strong>Playoffs</strong>
            </span>
            <span class="playoff-section-note">Win and advance</span>
          </header>

          <div class="time-sections" [class.time-sections--playoff]="phase.kind === 'playoff'">
          <section class="time-section" *ngFor="let timeGroup of phase.times">
            <div class="games-grid">
              <article class="game-card" [class.game-card--live]="isLiveGame(game)" [class.game-card--interactive]="isGameStatsEligible(game)"
                [attr.role]="isGameStatsEligible(game) ? 'button' : null" [attr.tabindex]="isGameStatsEligible(game) ? 0 : null"
                [attr.aria-label]="gameStatsAriaLabel(game)" (click)="openGameStats(game)" (keydown)="onGameCardKeydown($event, game)"
                *ngFor="let game of timeGroup.games; trackBy: trackByGame">
                <div class="card-topline">
                  <div class="meta-pill meta-pill--compact">
                    <mat-icon>event</mat-icon>
                    <span>{{ formatCardDate(game.game_date) }} · {{ formatGameTime(game.game_time || timeGroup.time) }}</span>
                  </div>
                  <div class="meta-pill meta-pill--inline meta-pill--date">
                    <mat-icon>location_on</mat-icon>
                    <span>{{ game.venue_court || 'Court TBD' }}</span>
                  </div>
                </div>

                <div class="tournament-live-state" *ngIf="isLiveGame(game)" aria-live="polite">
                  <span class="tournament-live-chip"><span class="tournament-live-dot"></span> Live</span>
                  <span>{{ livePeriodLabel(game) }}</span>
                  <strong>{{ liveClock(game) }}</strong>
                </div>

                <div class="card-body-row">
                  <div class="badge-row">
                    <div class="badge-row-main">
                      <span class="division-chip" [ngStyle]="divisionChipStyle(game)">{{ game.division_name || 'Tournament game' }} &middot; {{ gamePoolName(game) }}</span>
                    </div>
                  </div>

                  <div class="teams-section">
                    <div class="team-info">
                      <div class="team-flag-wrap">
                        <img *ngIf="teamLogoUrl(game.away_team_id) as logo" class="team-logo" [src]="logo" [alt]="game.away_team_name + ' logo'" (error)="markLogoBroken(game.away_team_id)" />
                        <span *ngIf="!teamLogoUrl(game.away_team_id)" class="team-flag-monogram">{{ teamMonogram(game.away_team_name) }}</span>
                      </div>
                      <div class="team-name">{{ game.away_team_name || 'TBD' }}</div>
                      <div class="team-score" *ngIf="gameHasVisibleScore(game)">{{ game.away_score }}</div>
                    </div>
                    <div class="vs-lockup"><span>VS</span><small *ngIf="isLiveGame(game)">Live</small><small *ngIf="!isLiveGame(game) && gameHasFinalScore(game)">Final</small></div>
                    <div class="team-info">
                      <div class="team-flag-wrap">
                        <img *ngIf="teamLogoUrl(game.home_team_id) as logo" class="team-logo" [src]="logo" [alt]="game.home_team_name + ' logo'" (error)="markLogoBroken(game.home_team_id)" />
                        <span *ngIf="!teamLogoUrl(game.home_team_id)" class="team-flag-monogram">{{ teamMonogram(game.home_team_name) }}</span>
                      </div>
                      <div class="team-name">{{ game.home_team_name || 'TBD' }}</div>
                      <div class="team-score" *ngIf="gameHasVisibleScore(game)">{{ game.home_score }}</div>
                    </div>
                  </div>
                </div>

                <div class="card-footer-meta">
                  <div class="venue-address" *ngIf="gameVenueName(game) || gameVenueAddress(game)">
                    <mat-icon>map</mat-icon>
                    <div>
                      <strong *ngIf="gameVenueName(game)">{{ gameVenueName(game) }}</strong>
                      <span *ngIf="gameVenueAddress(game)">{{ gameVenueAddress(game) }}</span>
                    </div>
                  </div>
                  <span *ngIf="isGameStatsEligible(game)" class="view-stats-affordance"><mat-icon>bar_chart</mat-icon> View stats</span>
                </div>
              </article>
            </div>
          </section>
          </div>
        </ng-container>
      </section>
    </div>
    <div *ngIf="tab === 'schedule' && !scheduleDayGroups().length" class="no-games">No upcoming games</div>

    <div *ngIf="tab === 'bracket'" class="bracket-view-tabs" aria-label="Tournament bracket views">
      <button type="button" [class.active]="bracketSection === 'schedule'" [attr.aria-pressed]="bracketSection === 'schedule'" (click)="bracketSection = 'schedule'">Schedule</button>
      <button type="button" [class.active]="bracketSection === 'ranking'" [attr.aria-pressed]="bracketSection === 'ranking'" (click)="bracketSection = 'ranking'">Ranking</button>
      <button type="button" [class.active]="bracketSection === 'playoff'" [attr.aria-pressed]="bracketSection === 'playoff'" (click)="bracketSection = 'playoff'">Playoff</button>
    </div>

    <div *ngIf="tab === 'bracket'" class="division-standings">
      <div *ngIf="bracketSection === 'schedule'" class="date-groups">
        <section class="date-section" *ngFor="let group of bracketScheduleDayGroups()">
          <div class="date-header">
            <div>
              <span class="date-kicker">{{ formatDatePart(group.date, 'weekday') }}</span>
              <h2>{{ formatDatePart(group.date, 'date') }}</h2>
            </div>
          </div>

          <ng-container *ngFor="let phase of schedulePhases(group.times)">
            <header class="pool-play-section-header" *ngIf="phase.kind === 'pool'">
              <span class="pool-play-section-icon" aria-hidden="true"><mat-icon>groups</mat-icon></span>
              <span class="pool-play-section-copy">
                <span>Group stage</span>
                <strong>Pool Play</strong>
              </span>
              <span class="pool-play-section-note">Compete for playoff seeding</span>
            </header>

            <header class="playoff-section-header" *ngIf="phase.kind === 'playoff'">
              <span class="playoff-section-icon" aria-hidden="true"><mat-icon>emoji_events</mat-icon></span>
              <span class="playoff-section-copy">
                <span>Knockout stage</span>
                <strong>Playoffs</strong>
              </span>
              <span class="playoff-section-note">Win and advance</span>
            </header>

            <div class="time-sections" [class.time-sections--playoff]="phase.kind === 'playoff'">
            <section class="time-section" *ngFor="let timeGroup of phase.times">
              <div class="games-grid">
                <article class="game-card" [class.game-card--live]="isLiveGame(game)" [class.game-card--interactive]="isGameStatsEligible(game)"
                  [attr.role]="isGameStatsEligible(game) ? 'button' : null" [attr.tabindex]="isGameStatsEligible(game) ? 0 : null"
                  [attr.aria-label]="gameStatsAriaLabel(game)" (click)="openGameStats(game)" (keydown)="onGameCardKeydown($event, game)"
                  *ngFor="let game of timeGroup.games; trackBy: trackByGame">
                  <div class="card-topline">
                    <div class="meta-pill meta-pill--compact">
                      <mat-icon>event</mat-icon>
                      <span>{{ formatCardDate(game.game_date) }} · {{ formatGameTime(game.game_time || timeGroup.time) }}</span>
                    </div>
                    <div class="meta-pill meta-pill--inline meta-pill--date">
                      <mat-icon>location_on</mat-icon>
                      <span>{{ game.venue_court || 'Court TBD' }}</span>
                    </div>
                  </div>

                  <div class="tournament-live-state" *ngIf="isLiveGame(game)" aria-live="polite">
                    <span class="tournament-live-chip"><span class="tournament-live-dot"></span> Live</span>
                    <span>{{ livePeriodLabel(game) }}</span>
                    <strong>{{ liveClock(game) }}</strong>
                  </div>

                  <div class="card-body-row">
                    <div class="badge-row">
                      <div class="badge-row-main">
                        <span class="division-chip" [ngStyle]="divisionChipStyle(game)">{{ game.division_name || 'Tournament game' }} &middot; {{ gamePoolName(game) }}</span>
                      </div>
                    </div>

                    <div class="teams-section">
                      <div class="team-info">
                        <div class="team-flag-wrap">
                          <img *ngIf="teamLogoUrl(game.away_team_id) as logo" class="team-logo" [src]="logo" [alt]="game.away_team_name + ' logo'" (error)="markLogoBroken(game.away_team_id)" />
                          <span *ngIf="!teamLogoUrl(game.away_team_id)" class="team-flag-monogram">{{ teamMonogram(game.away_team_name) }}</span>
                        </div>
                        <div class="team-name">{{ game.away_team_name || 'TBD' }}</div>
                        <div class="team-score" *ngIf="gameHasVisibleScore(game)">{{ game.away_score }}</div>
                      </div>
                      <div class="vs-lockup"><span>VS</span><small *ngIf="isLiveGame(game)">Live</small><small *ngIf="!isLiveGame(game) && gameHasFinalScore(game)">Final</small></div>
                      <div class="team-info">
                        <div class="team-flag-wrap">
                          <img *ngIf="teamLogoUrl(game.home_team_id) as logo" class="team-logo" [src]="logo" [alt]="game.home_team_name + ' logo'" (error)="markLogoBroken(game.home_team_id)" />
                          <span *ngIf="!teamLogoUrl(game.home_team_id)" class="team-flag-monogram">{{ teamMonogram(game.home_team_name) }}</span>
                        </div>
                        <div class="team-name">{{ game.home_team_name || 'TBD' }}</div>
                        <div class="team-score" *ngIf="gameHasVisibleScore(game)">{{ game.home_score }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="card-footer-meta">
                    <div class="venue-address" *ngIf="gameVenueName(game) || gameVenueAddress(game)">
                      <mat-icon>map</mat-icon>
                      <div>
                        <strong *ngIf="gameVenueName(game)">{{ gameVenueName(game) }}</strong>
                        <span *ngIf="gameVenueAddress(game)">{{ gameVenueAddress(game) }}</span>
                      </div>
                    </div>
                    <span *ngIf="isGameStatsEligible(game)" class="view-stats-affordance"><mat-icon>bar_chart</mat-icon> View stats</span>
                  </div>
                </article>
              </div>
            </section>
            </div>
          </ng-container>
        </section>
        <div *ngIf="!bracketScheduleDayGroups().length" class="no-games">No scheduled games yet.</div>
      </div>

      <ng-container *ngIf="bracketSection !== 'schedule'">
        <section class="division-standings-row" *ngFor="let division of divisionPoolStandings(); trackBy: trackByDivision">
          <div class="division-heading"><h3>{{ division.name }}</h3></div>
          <div class="division-playoff" *ngIf="bracketSection === 'playoff' && divisionBracket(division.divisionId) as bracket">
            <div class="division-playoff-title"><h3>Playoff</h3><span>Division playoff bracket</span></div>
            <app-playoff-bracket [bracket]="bracket" [editable]="false" [showSeeds]="false" [teamLogoUrls]="teamLogoUrls"></app-playoff-bracket>
          </div>
          <div *ngIf="bracketSection === 'ranking'" class="division-section-title"><h3>Ranking</h3></div>
          <div *ngIf="bracketSection === 'ranking'" class="pool-standings">
            <section class="pool-card" *ngFor="let pool of division.pools">
              <div class="pool-heading"><h3>{{ pool.name }}</h3></div>
              <div class="standings-table-wrap">
                <table class="standings-table">
                  <thead><tr><th>Rank</th><th>Team</th><th>W</th><th>L</th><th>+/-</th></tr></thead>
                  <tbody>
                    <tr *ngFor="let row of pool.rows">
                      <td class="standings-rank">{{ pool.isComplete ? row.rank : '' }}</td>
                      <td class="standings-team">
                        <span class="standings-team-logo" [class.standings-team-logo--placeholder]="!teamLogoUrl(row.teamId)">
                          <img *ngIf="teamLogoUrl(row.teamId) as logo" [src]="logo" [alt]="row.team + ' logo'" (error)="markLogoBroken(row.teamId)" />
                        </span>
                        <span>{{ row.team }}</span>
                      </td><td>{{ row.wins }}</td><td>{{ row.losses }}</td>
                      <td [class.positive-diff]="row.pointDiff > 0" [class.negative-diff]="row.pointDiff < 0">{{ row.pointDiff > 0 ? '+' : '' }}{{ row.pointDiff }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </section>
        <div *ngIf="!divisionPoolStandings().length" class="no-games">Pool standings are not available yet.</div>
      </ng-container>
    </div>

    <div *ngIf="tab === 'teams'" class="team-list"><span *ngFor="let team of teams()">{{ team.name }}</span></div>
  </section>
</main>
